import React, { createContext, useCallback, useEffect, useState } from "react";
import { TypeParams } from "../types";
import { makeApiRequest } from "./api";
import { notification } from "antd";

type NotificationType = "success" | "info" | "warning" | "error";

interface TUser {
  agb_accepted: string;
  data_protection: string;
  id: number;
  notification_open_surveys: number;
  nsuffix: string;
  schema: string;
  token_params: string;
  token_scope: string;
}

interface GlobalState {
  user: null | TUser;
  isAuthenticated: null | boolean;
  params: TypeParams | null;
}

interface GlobalContextType {
  state: GlobalState;
  setState: React.Dispatch<React.SetStateAction<GlobalState>>;
  isPageForbidden: () => boolean;
  isCMSBOXEditor: () => boolean;
  isLoginPage: () => boolean; // Properly typed as returning boolean
  isLogoutPage: () => boolean;
  gotoAfterLoginUrl: () => void;
  resetAfterLoginUrl: () => void;
  logout: () => void;
  navigate: (params: TypeParams, section: string) => void;
  authenticate: (parsed: any) => void;
  isRedirectedLogin: () => boolean;
  checkAuth: () => Promise<void>;
  isClientNumberPage: () => boolean;
  notificationContextHolder: any;
  notify: (type: NotificationType, title: string) => void;
}

export const GlobalStateContext = createContext<GlobalContextType>({
  state: {
    user: null,
    isAuthenticated: null,
    params: null as null | TypeParams,
  },
  setState: () => {},
  isPageForbidden: () => false,
  gotoAfterLoginUrl: () => {},
  resetAfterLoginUrl: () => {},
  isCMSBOXEditor: () => false,
  isLoginPage: () => false,
  isLogoutPage: () => false,
  isClientNumberPage: () => false,
  logout: () => {},
  navigate: () => {},
  authenticate: () => {},
  isRedirectedLogin: () => false,
  checkAuth: async () => {},
  notificationContextHolder: undefined,
  notify: () => {},
});

interface GlobalContextProps {
  children: React.ReactNode;
}
const GlobalStateProvider: React.FC<GlobalContextProps> = ({ children }) => {
  const [state, setState] = useState<GlobalState>({
    user: null,
    isAuthenticated: null,
    params: null,
  });

  // Define your functions
  const isLoginPage = useCallback((): boolean => {
    return !!state.params && state.params.page === "kundenbereich" && state.params.section === "login";
  }, [state.params]);

  const isLogoutPage = useCallback((): boolean => {
    return !!state.params && state.params.page === "kundenbereich" && state.params.section === "logout";
  }, [state.params]);

  const isClientNumberPage = useCallback((): boolean => {
    return !!state.params && state.params.page === "kundenbereich" && state.params.section === "clientnumber";
  }, [state.params]);

  // const isCMSBOXEditor = useCallback((): boolean => {
  //   return document.body && document.body.id === "edit";
  // }, []);

  const isCMSBOXEditor = useCallback((): boolean => {
    // console.log(window.self !== window.top, "DEBUG: isCMSBOXEditor");
    // return true;
    return window.self !== window.top;
  }, []);

  const isRedirectedLogin = useCallback((): boolean => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has("redirected");
  }, []);

  const resetAfterLoginUrl = useCallback((): void => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams && urlParams.has("redirected")) {
      // console.log("DEBUG: has been redirected to login page");
    } else {
      localStorage.removeItem("afterLoginUrl");
    }
  }, []);

  const gotoAfterLoginUrl = useCallback((): void => {
    if (localStorage.getItem("afterLoginUrl")) {
      const url = localStorage.getItem("afterLoginUrl") || "/";
      resetAfterLoginUrl();
      window.location.href = url;
    }
  }, [resetAfterLoginUrl]);

  const logout = useCallback((): void => {
    setState((prevState: any) => ({ ...prevState, user: undefined, isAuthenticated: false }));
    localStorage.removeItem("X-SESSION-ID");
  }, []);

  const [notificationAPI, notificationContextHolder] = notification.useNotification();

  const notify = useCallback(
    (type: NotificationType, title: string): void => {
      const openNotification = (type: NotificationType, title: string) => {
        notificationAPI[type]({
          message: title,
          duration: 10,
          // description:
          //   "This is the content of the notification. This is the content of the notification. This is the content of the notification.",
        });
      };

      openNotification(type, title);
    },
    [notificationAPI]
  );

  const navigate = useCallback((params: TypeParams, section: string): void => {
    if (params.embeddedScript) {
      const currentUrl = window.location.pathname;
      const urlParts = currentUrl.split("/");
      urlParts[urlParts.length - 1] = section;
      const newUrl = urlParts.join("/");
      window.location.href = newUrl;
    } else {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set("section", section);
      const showMenu = params.showMenu ? params.showMenu : state.params ? state.params.showMenu : 1;
      if (showMenu) {
        urlParams.set("showMenu", params.showMenu ? "1" : "0");
      }

      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.replaceState({}, "", newUrl);
      setState((prevState: any) => ({ ...prevState, params: { ...prevState.params, section, showMenu } }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const authenticate = useCallback((user: any): void => {
    setState((prevState: any) => ({ ...prevState, user, isAuthenticated: true }));
  }, []);

  const isPageForbidden = useCallback((): boolean => {
    const allowOnSiteLogin = !!(state.params && state.params.allowOnSiteLogin);
    // console.log("DEBUG: isPageForbidden", state.isAuthenticated, isLoginPage(), allowOnSiteLogin);
    if (state.isAuthenticated !== false || isLoginPage() || allowOnSiteLogin) {
      return false;
    }

    if (isCMSBOXEditor()) {
      // console.log("DEBUG ...inside the CMSBOX Editor: the page is not redirected to the login page");
      // DO NOT REDIRECT TO LOGIN PAGE:
      if (state.params && state.params.linkLoginPage) {
        console.warn("...the page is redirected to the loging page ", state.params.linkLoginPage);
      } else {
        console.warn("...the page is redirected to the home page '/'. (no params.linkLoginPage is set)");
      }
      return false;
    }

    // REDIRECT TO LOGIN PAGE (or homepage "/")
    // console.log("DEBUG ...outside the CMSBOX Editor: the page is redirected");
    localStorage.setItem("afterLoginUrl", window.location.href);
    if (state.params && state.params.linkLoginPage) {
      const url = new URL(state.params.linkLoginPage, window.location.origin);
      url.searchParams.append("redirected", "1");
      url.searchParams.append("backend", state.params.backend);
      window.location.href = url.toString();
      return true;
    }

    console.error("params.loginPage is not set: redirect to homepage");
    window.location.href = "/";
    return true;
  }, [isCMSBOXEditor, isLoginPage, state.isAuthenticated, state.params]);

  const checkAuth = useCallback(async () => {
    if (state.isAuthenticated !== null) {
      return;
    }
    const action = "REACT_CLIENTAREA";
    return makeApiRequest(state.params!, action, {}, true).then((response) => {
      if (response) {
        const parsed = JSON.parse(response);
        if (!parsed || parsed.MESSAGE !== "OK") {
          logout();
        } else {
          // console.log("DEBUG: checkAuth", parsed, response);
          authenticate(parsed.CONTENT);
        }
        return parsed;
      } else {
        return null;
      }
    });
  }, [authenticate, logout, state.isAuthenticated, state.params]);

  useEffect(() => {
    if (state.isAuthenticated) {
      gotoAfterLoginUrl();
    }
  }, [gotoAfterLoginUrl, state.isAuthenticated]);

  useEffect(() => {
    if (isLogoutPage()) {
      logout();
    }
  }, [state.params, state.isAuthenticated, isLogoutPage, logout]);

  return (
    <GlobalStateContext.Provider
      value={{
        state,
        setState,
        isPageForbidden,
        gotoAfterLoginUrl,
        resetAfterLoginUrl,
        isCMSBOXEditor,
        isLoginPage,
        isLogoutPage,
        logout,
        navigate,
        authenticate,
        isRedirectedLogin,
        checkAuth,
        isClientNumberPage,
        notificationContextHolder,
        notify,
      }}
    >
      {children}
    </GlobalStateContext.Provider>
  );
};

export default GlobalStateProvider;
